/**
 * @lodex-pro/web-dispatch — entry point.
 *
 * Phase 4 / Plan 04-10 — bootstraps Amplify v6 BEFORE the React tree mounts so
 * `cognitoUserPoolsTokenProvider.setKeyValueStorage(...)` is wired before any
 * call to `signIn`, `confirmSignIn`, `fetchAuthSession`, etc.
 *
 * Tenant type literal: `hauler` (web-dispatch serves hauler dispatchers only).
 * Pools come from `VITE_COGNITO_POOL_CONFIG_JSON` (a JSON array of 4 entries in
 * CDK iteration order — hauler, operator, disposal, platform — see
 * `packages/shared/src/auth/amplify-config.ts` AMPLIFY_TENANT_TYPE_ORDER).
 *
 * The empty-pools array fallback (`[]`) is intentional: in test/preview builds
 * or when the env var is not provisioned, `selectPoolByTenantType` throws a
 * clear error indicating a misconfigured pool list rather than silently
 * picking the wrong pool. Per the plan, the API base URL also falls back to
 * `http://localhost:3000` for local dev when `VITE_API_BASE_URL` is unset.
 *
 * Spec: CONTEXT D-03 (pool config positional array); D-05 (Amplify v6); D-07
 *       (cookie exchange); RESEARCH § Pattern 4 (Amplify storage adapter wiring).
 */

// CSS imports MUST be first (RESEARCH Pitfall 1 — Mapbox worker + CSS before component imports)
import './styles.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { bootstrapWebAmplify, QueryProvider, ApiOptsProvider } from '@lodex-pro/web-shared';
import type { PoolConfigEntry } from '@lodex-pro/shared/auth/amplify-config';

import { App } from './App';

const POOLS_RAW = (import.meta.env['VITE_COGNITO_POOL_CONFIG_JSON'] as string | undefined) ?? '[]';
const API_BASE_URL =
  (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? 'http://localhost:3000';
const pools = JSON.parse(POOLS_RAW) as ReadonlyArray<PoolConfigEntry>;

bootstrapWebAmplify({
  pools,
  tenantType: 'hauler',
  apiBaseUrl: API_BASE_URL,
});

const rootElement = document.getElementById('root');
if (rootElement === null) {
  throw new Error('Root element #root not found in index.html');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <QueryProvider>
      <ApiOptsProvider opts={{ baseUrl: API_BASE_URL }}>
        <App />
      </ApiOptsProvider>
    </QueryProvider>
  </React.StrictMode>,
);
